/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/microsoft-teams" */ '@atlassian/logo-third-party/microsoft-teams'
		).then((m) => m.MicrosoftTeamsIcon),
	{ moduleId: '@atlassian/logo-third-party/microsoft-teams' },
);

const LazyTeamsIconComponent = lazyForPaint(
	() => import(/* webpackChunkName: "icon-teams" */ './utils').then((m) => m.TeamsIcon),
	{ moduleId: '@atlassian/rovo-connectors-config/teams/icon' },
);

const LazyTeamsSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-teams-search-result" */ './utils').then(
			(m) => m.TeamsSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/teams/search-result-icon' },
);

const LazyTeamsIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-teams-borderless" */ './utils').then(
			(m) => m.TeamsIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/teams/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const TeamsIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyTeamsIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const TeamsSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyTeamsSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const TeamsIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyTeamsIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
